import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function StudentsPromote() {
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialogBatch, setDialogBatch] = useState(null);
  const [undoBatch, setUndoBatch] = useState(null);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const ordinal = (value) => {
    const number = Number(value);
    const suffix =
      number % 100 >= 11 && number % 100 <= 13
        ? "th"
        : { 1: "st", 2: "nd", 3: "rd" }[number % 10] || "th";
    return `${number}${suffix}`;
  };
  const api = `${process.env.REACT_APP_API_BASE_URL}/api/students-promote/${user?.client_id}/${user?.college_id}`;
  const headers = { Authorization: `Bearer ${sessionStorage.getItem("token")}` };

  const load = async () => {
    try {
      const response = await axios.get(api, { headers });
      setRows(response.data?.data || []);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to load admitted batches",
      });
    }
  };
  useEffect(() => {
    if (user?.client_id && user?.college_id) load();
  }, [user?.client_id, user?.college_id]);

  const batches = useMemo(() => {
    const grouped = new Map();
    rows.forEach((row) => {
      if (!grouped.has(row.admittedbatch_id))
        grouped.set(row.admittedbatch_id, {
          ...row,
          id: row.admittedbatch_id,
          can_promote: false,
          can_unpromote: false,
        });
      const batch = grouped.get(row.admittedbatch_id);
      batch.can_promote = batch.can_promote || row.can_promote;
      batch.can_unpromote = batch.can_unpromote || row.can_unpromote;
    });
    return [...grouped.values()];
  }, [rows]);
  const dialogRows = useMemo(
    () => rows.filter((row) => row.admittedbatch_id === dialogBatch),
    [rows, dialogBatch]
  );
  const selectedBatch = batches.find((batch) => batch.admittedbatch_id === dialogBatch);
  const undoBatchRow = batches.find((batch) => batch.admittedbatch_id === undoBatch);
  const selectableIds = dialogRows
    .filter((row) => row.can_promote && row.student_count > 0)
    .map((row) => row.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

  const openPromote = (batchId) => {
    const batch = batches.find((item) => item.admittedbatch_id === batchId);
    if (batch && !batch.has_promotion_data) {
      setMessage({ type: "error", text: "Batch students not promoted" });
      return;
    }
    setDialogBatch(batchId);
    setSelected([]);
    setDetailPage(0);
  };
  const confirm = async () => {
    const selectedRows = dialogRows.filter((row) => selected.includes(row.id));
    if (!dialogBatch) {
      setMessage({ type: "error", text: "Select a batch before confirming promotion" });
      return;
    }
    if (!selectedRows.length) {
      setMessage({ type: "error", text: "Select at least one branch with active students" });
      return;
    }
    if (selectedRows.some((row) => !row.can_promote || row.student_count <= 0)) {
      setMessage({
        type: "error",
        text: "Review the selected branches; some are not eligible for promotion",
      });
      return;
    }
    setBusy(true);
    try {
      const payload = selectedRows.map((row) => ({
        admittedbatch_id: row.admittedbatch_id,
        program_id: row.program_id,
        branch_id: row.branch_id,
        current_year: row.current_year,
        current_semester: row.current_semester,
        year_number: row.next_year,
        semester_number: row.next_semester,
      }));
      const response = await axios.post(
        api,
        { rows: payload, created_by: user?.user_email || user?.user_name },
        { headers }
      );
      setMessage({
        type: "success",
        text: `${response.data?.data?.promoted || 0} student(s) promoted successfully`,
      });
      setDialogBatch(null);
      setSelected([]);
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to promote students",
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmUndo = async () => {
    if (!undoBatch || !undoBatchRow?.can_unpromote) {
      setMessage({ type: "error", text: "This batch has no promotion available to undo" });
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(
        `${api}/${undoBatch}/undo`,
        { created_by: user?.user_email || user?.user_name },
        { headers }
      );
      setMessage({
        type: "success",
        text: `${response.data?.data?.reverted || 0} student promotion(s) reverted successfully`,
      });
      setUndoBatch(null);
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Unable to undo the batch promotion",
      });
    } finally {
      setBusy(false);
    }
  };

  const batchColumns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 75,
      sortable: false,
      valueGetter: (p) => p.api.getRowIndex(p.id) + 1,
    },
    { field: "admittedbatch", headerName: "Batch", minWidth: 150, flex: 1 },
    {
      field: "current_year",
      headerName: "Current Year",
      minWidth: 120,
      flex: 0.7,
      valueFormatter: (p) => (p.value ? `${ordinal(p.value)} Year` : "—"),
    },
    {
      field: "current_semester",
      headerName: "Semester",
      minWidth: 130,
      flex: 0.8,
      valueFormatter: (p) => (p.value ? `${ordinal(p.value)} Semester` : "—"),
    },
    {
      field: "action",
      headerName: "Actions",
      minWidth: 235,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          {p.row.can_promote || !p.row.has_promotion_data ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => openPromote(p.row.admittedbatch_id)}
              sx={{
                color: "#fff",
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Promote
            </Button>
          ) : null}
          {p.row.can_unpromote ? (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setUndoBatch(p.row.admittedbatch_id)}
              sx={{
                color: "#fff",
                backgroundColor: "#d32f2f",
                "&:hover": { color: "#fff", backgroundColor: "#b71c1c" },
              }}
            >
              Undo
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];
  const detailColumns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 70,
      sortable: false,
      valueGetter: (p) => p.api.getRowIndex(p.id) + 1,
    },
    { field: "admittedbatch", headerName: "Batch", minWidth: 125, flex: 1 },
    { field: "program", headerName: "Program", minWidth: 130, flex: 1 },
    { field: "branch", headerName: "Branch", minWidth: 130, flex: 1 },
    {
      field: "target",
      headerName: "Promote To (Year / Semester)",
      minWidth: 210,
      valueGetter: (p) =>
        p.row.can_promote ? `${p.row.next_year} / ${p.row.next_semester}` : "Final semester",
    },
    {
      field: "students",
      headerName: "Active Students",
      width: 125,
      valueGetter: (p) => p.row.student_count,
    },
  ];

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Promote Students
      </Typography>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setMessage(null)}
      >
        {message ? (
          <Alert
            severity={message.type}
            onClose={() => setMessage(null)}
            sx={{
              width: "100%",
              color: "#fff",
              backgroundColor: "#1976d2",
              "& .MuiAlert-icon, & .MuiAlert-action, & .MuiIconButton-root": { color: "#fff" },
            }}
          >
            {message.text}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ height: 430, width: "100%" }}>
          <DataGrid
            rows={batches}
            columns={batchColumns}
            getRowId={(row) => row.id}
            pagination
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>
      <Dialog
        open={dialogBatch !== null}
        onClose={() => !busy && setDialogBatch(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: "100%", maxHeight: "90dvh" } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          {selectedBatch
            ? `Promote Students - Batch ${selectedBatch.admittedbatch}`
            : "Promote Students"}
          <IconButton
            aria-label="Close"
            color="error"
            disabled={busy}
            onClick={() => setDialogBatch(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "70dvh", overflowY: "auto" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              onChange={(e) => setSelected(e.target.checked ? selectableIds : [])}
            />
            <Typography variant="body2">Select all eligible branches</Typography>
          </Stack>
          <Box sx={{ height: "clamp(180px, 45dvh, 690px)", width: "100%" }}>
            <DataGrid
              rows={dialogRows}
              columns={detailColumns}
              getRowId={(row) => row.id}
              checkboxSelection
              isRowSelectable={(params) => params.row.can_promote && params.row.student_count > 0}
              selectionModel={selected}
              onSelectionModelChange={(newSelection) => setSelected(newSelection)}
              pagination
              page={detailPage}
              pageSize={detailPageSize}
              onPageChange={setDetailPage}
              onPageSizeChange={(size) => {
                setDetailPageSize(size);
                setDetailPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              disableSelectionOnClick
              sx={{
                "& .MuiDataGrid-checkboxInput": { color: "#0d47a1", opacity: 1 },
                "& .MuiDataGrid-checkboxInput.Mui-disabled": { color: "#616161", opacity: 1 },
                "& .MuiDataGrid-checkboxInput .MuiSvgIcon-root": { fontSize: 26, opacity: 1 },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="contained"
            startIcon={<CloseIcon />}
            onClick={() => setDialogBatch(null)}
            disabled={busy}
            sx={{
              color: "#fff",
              backgroundColor: "#d32f2f",
              "&:hover": { color: "#fff", backgroundColor: "#b71c1c" },
              "&.Mui-disabled": { color: "#fff", backgroundColor: "#d32f2f", opacity: 0.65 },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirm}
            disabled={busy || selected.length === 0}
            sx={{
              color: "#fff",
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            {busy ? "Promoting..." : "Confirm Promote"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={undoBatch !== null}
        onClose={() => !busy && setUndoBatch(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Undo Latest Batch Promotion</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {undoBatchRow
              ? `Undo the latest promotion for batch ${undoBatchRow.admittedbatch}? The latest student promotion entries will be removed and the batch will return to its previous configured semester.`
              : "Undo the latest promotion for this batch?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="contained"
            startIcon={<CloseIcon />}
            onClick={() => setUndoBatch(null)}
            disabled={busy}
            sx={{
              color: "#fff",
              backgroundColor: "#d32f2f",
              "&:hover": { color: "#fff", backgroundColor: "#b71c1c" },
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmUndo}
            disabled={busy || !undoBatchRow?.can_unpromote}
          >
            {busy ? "Undoing..." : "Confirm Undo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
